import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as THREE from "three";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  AlertTriangle,
  Loader2,
  RotateCcw,
} from "lucide-react";

/**
 * Slim 360° equirectangular player for the editor preview.
 *
 * Differences vs. main `Player360`:
 * - Drag-only (no gyro — useless on desktop and would steal touch events in
 *   the editor).
 * - Controls are always visible (no auto-hide) — needed for precise scrubbing.
 * - Respects `startSec` / `endSec` boundaries: snaps currentTime into bounds
 *   on src/range change, auto-pauses at `endSec`, restart-button jumps to
 *   `startSec`.
 * - Exposes an imperative handle so the parent editor can read
 *   `getCurrentTime()` for the "Anfang hier setzen" / "Ende hier setzen"
 *   buttons.
 * - Shows a clear warn-block when the source's mobile transcode is missing
 *   (`mobileReady=false`) — editor scrubbing on a full-res 5760×2880 source
 *   is slow and stutters; the user needs to know why.
 */
const Player360Editor = forwardRef(function Player360Editor(
  {
    src,
    startSec = 0,
    endSec = Infinity,
    onTimeUpdate,
    mobileReady = true,
    notReadyMessage = "",
  },
  ref
) {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);

  const rafRef = useRef(0);
  const orientRef = useRef({ yaw: 0, pitch: 0 });
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const fovRef = useRef(75);
  const pinchRef = useRef({ active: false, startDist: 0, startFov: 75 });
  const boundsRef = useRef({ start: startSec, end: endSec });

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(startSec);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [webglError, setWebglError] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [videoError, setVideoError] = useState("");

  // Keep bounds in a ref so timeupdate (frequent) reads them without
  // re-binding the listener every render.
  useEffect(() => {
    boundsRef.current = { start: startSec, end: endSec };
  }, [startSec, endSec]);

  // ---- Three.js scene ----
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!wrapper || !canvas || !video) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      wrapper.clientWidth / wrapper.clientHeight,
      0.1,
      1100,
    );
    camera.position.set(0, 0, 0.01);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    } catch (err) {
      console.warn("WebGL init failed:", err);
      setWebglError(true);
      return undefined;
    }
    if (!renderer.getContext()) {
      setWebglError(true);
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);

    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.format = THREE.RGBAFormat;

    const material = new THREE.MeshBasicMaterial({ map: texture });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const tick = () => {
      const { yaw, pitch } = orientRef.current;
      const e = new THREE.Euler(pitch, yaw, 0, "YXZ");
      camera.quaternion.setFromEuler(e);
      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const ro = new ResizeObserver(() => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    });
    ro.observe(wrapper);

    // ---- Zoom: mouse wheel (desktop) + 2-finger pinch (mobile) ----
    const MIN_FOV = 30;
    const MAX_FOV = 100;
    const applyFov = (target) => {
      const next = Math.max(MIN_FOV, Math.min(MAX_FOV, target));
      if (next === camera.fov) return;
      camera.fov = next;
      fovRef.current = next;
      camera.updateProjectionMatrix();
    };
    const onWheel = (ev) => {
      ev.preventDefault();
      applyFov(camera.fov + ev.deltaY * 0.05);
    };
    const pinchDist = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );
    const onTouchStart = (ev) => {
      if (ev.touches.length === 2) {
        pinchRef.current = {
          active: true,
          startDist: pinchDist(ev.touches),
          startFov: camera.fov,
        };
        dragRef.current.active = false;
        ev.preventDefault();
      }
    };
    const onTouchMove = (ev) => {
      if (!pinchRef.current.active || ev.touches.length !== 2) return;
      const dist = pinchDist(ev.touches);
      if (!dist) return;
      const ratio = dist / pinchRef.current.startDist;
      if (!ratio || !Number.isFinite(ratio)) return;
      applyFov(pinchRef.current.startFov / ratio);
      ev.preventDefault();
    };
    const onTouchEnd = (ev) => {
      if (ev.touches.length < 2) pinchRef.current.active = false;
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);
    canvas.addEventListener("touchcancel", onTouchEnd);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("touchcancel", onTouchEnd);
      geometry.dispose();
      material.dispose();
      texture.dispose();
      renderer.dispose();
    };
  }, []);

  // ---- Drag pointer handlers ----
  const onPointerDown = (e) => {
    if (pinchRef.current.active) return;
    dragRef.current.active = true;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (pinchRef.current.active) return;
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    const w = wrapperRef.current?.clientWidth || 1;
    const h = wrapperRef.current?.clientHeight || 1;
    orientRef.current.yaw -= (dx / w) * Math.PI;
    orientRef.current.pitch -= (dy / h) * Math.PI;
    const maxPitch = THREE.MathUtils.degToRad(85);
    orientRef.current.pitch = Math.max(-maxPitch, Math.min(maxPitch, orientRef.current.pitch));
  };
  const onPointerUp = (e) => {
    dragRef.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // ---- Video element lifecycle ----
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = () => {
      const t = v.currentTime;
      setCurrentTime(t);
      onTimeUpdate?.(t);
      // Enforce end_sec: pause if we cross over.
      const { end, start } = boundsRef.current;
      if (Number.isFinite(end) && t >= end - 0.02) {
        v.pause();
        try { v.currentTime = end; } catch { /* seek failed */ }
      }
      // Defensive: also clamp on start.
      if (t < start - 0.05) {
        try { v.currentTime = start; } catch { /* seek failed */ }
      }
    };
    const onMeta = () => {
      setDuration(v.duration || 0);
      setVideoError("");
      // Snap to startSec on first load of this src.
      try { v.currentTime = boundsRef.current.start || 0; } catch { /* noop */ }
    };
    const onPlay = () => { setIsPlaying(true); setHasStarted(true); setVideoError(""); };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onCanPlay = () => setBuffering(false);
    const onStalled = () => setBuffering(true);
    const onLoadedData = () => { setVideoError(""); setBuffering(false); };
    const onErr = () => {
      const code = v.error?.code;
      if (code === 3 || code === 4) {
        setVideoError("Video konnte nicht geladen werden.");
      }
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("error", onErr);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("stalled", onStalled);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("error", onErr);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("stalled", onStalled);
    };
  }, [onTimeUpdate]);

  // Reset on src change.
  useEffect(() => {
    setHasStarted(false);
    setBuffering(false);
    setVideoError("");
    setCurrentTime(startSec);
  }, [src, startSec]);

  // When startSec changes while paused, snap currentTime into [start,end].
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !v.paused) return;
    const { start, end } = boundsRef.current;
    if (v.currentTime < start || v.currentTime > end) {
      try { v.currentTime = start; } catch { /* noop */ }
    }
  }, [startSec, endSec]);

  // ---- Imperative handle for parent ----
  useImperativeHandle(ref, () => ({
    getCurrentTime() {
      return videoRef.current?.currentTime ?? 0;
    },
    seekTo(seconds) {
      const v = videoRef.current;
      if (!v) return;
      try { v.currentTime = Math.max(0, seconds || 0); } catch { /* noop */ }
    },
    play() {
      const v = videoRef.current;
      if (!v) return;
      const { start, end } = boundsRef.current;
      if (v.currentTime < start || v.currentTime >= end) {
        try { v.currentTime = start; } catch { /* noop */ }
      }
      v.play().catch(() => { /* autoplay blocked */ });
    },
    pause() {
      videoRef.current?.pause();
    },
  }), []);

  // ---- Controls ----
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const { start, end } = boundsRef.current;
      if (v.currentTime < start || v.currentTime >= end) {
        try { v.currentTime = start; } catch { /* noop */ }
      }
      v.play().catch(() => setVideoError("Wiedergabe blockiert. Erneut tippen."));
    } else {
      v.pause();
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const restart = () => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = boundsRef.current.start || 0; } catch { /* noop */ }
  };

  const onSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX != null ? e.clientX : e.touches?.[0]?.clientX;
    if (clientX == null) return;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let t = pct * duration;
    // Snap into bounds if we have them.
    const { start, end } = boundsRef.current;
    if (Number.isFinite(end)) t = Math.min(t, end - 0.02);
    if (Number.isFinite(start)) t = Math.max(t, start);
    try { v.currentTime = t; } catch { /* noop */ }
  };

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    if (!document.fullscreenElement) {
      wrap.requestFullscreen?.().catch(() => { /* noop */ });
    } else {
      document.exitFullscreen?.().catch(() => { /* noop */ });
    }
  };

  const fmt = (s) => {
    if (!s || !Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // Progress fill should track current position relative to the FULL source
  // duration (so the user can see where in the original they are), but we
  // also mark the trimmed range with a brighter band.
  const totalPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const startPct = duration > 0 ? (startSec / duration) * 100 : 0;
  const endPct = duration > 0 ? (Math.min(endSec, duration) / duration) * 100 : 100;

  if (webglError) {
    return (
      <div
        ref={wrapperRef}
        data-testid="player360-editor-wrapper"
        className="relative w-full bg-black select-none rounded-xl overflow-hidden aspect-video flex items-center justify-center"
        style={{ minHeight: 320 }}
      >
        <div className="text-center px-6 py-8 max-w-md" data-testid="player360-editor-webgl-fallback">
          <div className="inline-flex w-12 h-12 items-center justify-center rounded-2xl bg-[#1f1f23] mb-4">
            <AlertTriangle size={22} className="text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Dein Browser unterstützt WebGL nicht</h3>
          <p className="text-sm text-[#9a9aa3] mt-2 leading-relaxed">
            Für die 360°-Darstellung benötigen wir WebGL. Bitte aktualisiere
            deinen Browser oder aktiviere Hardware-Beschleunigung.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      data-testid="player360-editor-wrapper"
      className="relative w-full bg-black select-none rounded-xl overflow-hidden aspect-video group"
      style={{ minHeight: 320 }}
    >
      <video
        ref={videoRef}
        src={src}
        playsInline
        muted={muted}
        preload="auto"
        className="hidden"
        data-testid="player360-editor-video"
      />

      <canvas
        ref={canvasRef}
        data-testid="player360-editor-canvas"
        className="absolute inset-0 w-full h-full touch-none cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      {/* Tap-to-play overlay until first play */}
      {!hasStarted && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-[2px] hover:bg-black/40 transition-colors"
          aria-label="Video abspielen"
          data-testid="player360-editor-tap-to-play"
        >
          <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 border border-white/20 backdrop-blur-md group-hover:scale-105 transition-transform">
            <Play size={32} className="text-white translate-x-0.5" fill="currentColor" />
          </span>
        </button>
      )}

      {/* Buffering spinner */}
      {hasStarted && buffering && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
          data-testid="player360-editor-buffering"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/55 backdrop-blur text-white text-sm">
            <Loader2 size={18} className="animate-spin" />
            Lädt…
          </span>
        </div>
      )}

      {videoError && (
        <div
          className="absolute top-3 left-3 right-3 mx-auto max-w-md bg-black/80 text-red-300 text-xs px-3 py-2 rounded-md z-30"
          data-testid="player360-editor-error"
        >
          {videoError}
        </div>
      )}

      {/* Mobile-not-ready warning */}
      {!mobileReady && (
        <div
          className="absolute top-3 left-3 right-3 mx-auto max-w-lg bg-amber-500/95 text-black text-xs px-3 py-2 rounded-md z-30 flex items-start gap-2 shadow-lg"
          data-testid="player360-editor-not-ready"
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div className="leading-snug">
            <strong>Diese Datei wird noch für den Editor optimiert.</strong>
            <span className="block mt-0.5">
                {notReadyMessage ||
                  'Vorschau läuft mit Original-Qualität und kann ruckeln — bitte 1–3 Min warten, bis das grüne „Mobile bereit"-Badge erscheint.'}
            </span>
          </div>
        </div>
      )}

      {/* Bottom controls (always visible in editor) */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-3 pb-3 pt-10 z-10">
        <div
          className="relative h-3 bg-white/15 rounded-full cursor-pointer mb-3"
          onClick={onSeek}
          onTouchStart={onSeek}
          data-testid="player360-editor-progress-bar"
        >
          {/* Trimmed range marker (subtle white band so user sees where the clip lives) */}
          <div
            className="absolute top-0 bottom-0 bg-white/25 rounded-full"
            style={{ left: `${startPct}%`, right: `${100 - endPct}%` }}
            data-testid="player360-editor-trim-range"
          />
          {/* Playhead fill */}
          <div
            className="absolute top-0 bottom-0 left-0 rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] transition-[width] duration-100"
            style={{ width: `${totalPct}%` }}
            data-testid="player360-editor-progress-fill"
          />
        </div>

        <div className="flex items-center justify-between gap-2 text-neutral-100">
          <div className="flex items-center gap-1.5">
            <IconBtn onClick={togglePlay} testId="player360-editor-play-button" label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? <Pause size={20} /> : <Play size={20} fill="currentColor" />}
            </IconBtn>
            <IconBtn onClick={restart} testId="player360-editor-restart-button" label="Zum Trim-Anfang springen">
              <RotateCcw size={18} />
            </IconBtn>
            <IconBtn onClick={toggleMute} testId="player360-editor-mute-button" label={muted ? "Ton an" : "Stumm"}>
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </IconBtn>
            <span className="text-xs text-neutral-300 tabular-nums ml-1" data-testid="player360-editor-time-display">
              {fmt(currentTime)} / {fmt(duration)}
            </span>
          </div>
          <IconBtn onClick={toggleFullscreen} testId="player360-editor-fullscreen-button" label={isFullscreen ? "Vollbild beenden" : "Vollbild"}>
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </IconBtn>
        </div>
      </div>
    </div>
  );
});

function IconBtn({ children, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors text-white"
    >
      {children}
    </button>
  );
}

export default Player360Editor;
